import * as pc from "playcanvas";
import type { GameClient } from "../net/GameClient";
import type { FirstPersonController } from "./FirstPersonController";
import type { CharacterFactory } from "./CharacterFactory";
import type { RemoteCharacter } from "./RemoteCharacter";

/**
 * LocalPlayerShadow — gives the first-person player a body that casts a
 * shadow but is invisible to the camera, so looking down you see your own
 * animated shadow on the ground.
 *
 * It's the same skinned Mixamo character used for opponents, parented to
 * the FPC body (follows position + yaw, never pitch) and driven by the
 * local movement state. It stays on the World layer (so the sun's shadow
 * pass picks it up like any other caster), but its materials write neither
 * colour nor depth — so the forward camera draws nothing, while the shadow
 * pass (a separate depth-only render) still casts the shadow. No body in
 * front of the view, no double-arms with the weapon viewmodel, just a
 * clean shadow.
 */
export class LocalPlayerShadow {
  private readonly app: pc.AppBase;
  private readonly client: GameClient;
  private readonly fpc: FirstPersonController;
  private readonly factory: CharacterFactory;
  private character: RemoteCharacter | null = null;
  private prevX = 0;
  private prevZ = 0;
  private readonly tickFn: (dt: number) => void;

  constructor(app: pc.AppBase, client: GameClient, fpc: FirstPersonController, factory: CharacterFactory) {
    this.app = app;
    this.client = client;
    this.fpc = fpc;
    this.factory = factory;
    this.tickFn = (dt: number) => this.update(dt);
    this.app.on("update", this.tickFn);
  }

  destroy(): void {
    this.app.off("update", this.tickFn);
    this.character?.destroy();
  }

  private tryCreate(): void {
    if (this.character || !this.factory.ready) return;
    const team = this.client.getLatestSelf()?.team ?? "ct";
    const char = this.factory.create(team);
    if (!char) return;
    this.character = char;

    const body = this.fpc.getBodyEntity();
    char.entity.setLocalEulerAngles(0, 180, 0); // Mixamo +Z → our -Z facing
    char.entity.setLocalPosition(0, 0, 0);
    body.addChild(char.entity);

    // Shadow-only: keep it on the World layer (so the sun shadows it like
    // any caster) but make every material write neither colour nor depth —
    // invisible in the camera, still casts a shadow.
    const renders = char.entity.findComponents("render") as pc.RenderComponent[];
    for (const r of renders) {
      r.castShadows = true;
      r.receiveShadows = false;
      for (const mi of r.meshInstances) {
        const m = mi.material as pc.StandardMaterial;
        const bs = new pc.BlendState();
        bs.setColorWrite(false, false, false, false);
        m.blendState = bs;
        m.depthWrite = false;
        m.update();
      }
    }

    const p = body.getPosition();
    this.prevX = p.x;
    this.prevZ = p.z;
  }

  private update(dt: number): void {
    if (!this.character) {
      this.tryCreate();
      return;
    }

    const self = this.client.getLatestSelf();
    const alive = (self?.health ?? 1) > 0;
    this.character.entity.enabled = alive; // no shadow while dead/spectating
    if (!alive) return;

    const body = this.fpc.getBodyEntity();
    const p = body.getPosition();
    const invDt = dt > 0 ? 1 / dt : 0;
    const vx = (p.x - this.prevX) * invDt;
    const vz = (p.z - this.prevZ) * invDt;
    this.prevX = p.x;
    this.prevZ = p.z;

    const yaw = body.getEulerAngles().y;
    const ms = this.fpc.getMovementState();
    const knife = self?.weaponId === "knife";
    // Fire the jump clip the instant the jump executes (Space press), not
    // when the body becomes airborne a few frames later; and end it exactly
    // on landing so the pose matches the real ~0.7 s air time.
    if (this.fpc.consumeJustJumped()) this.character.playJump();
    if (!ms.airborne) this.character.notifyGrounded();
    this.character.applyMotion(vx, vz, yaw, ms.walking, knife, ms.crouching);
  }
}
